import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  BarChart3,
  Check,
  ChevronRight,
  Mail,
  PartyPopper,
  RotateCcw,
  ScanLine,
  Sparkles,
  Trophy,
  UserPlus,
  X,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@lib/utils';
// eslint-disable-next-line no-restricted-imports -- I-56 istisnası: ai ↔ rehber barrel döngüsü (AylikRaporButton → @features/rehber)
import { AiKrediChip } from '@features/ai/AiKrediChip';
import { useMusteriler } from '@features/musteri';
import {
  REHBER_ADIMLAR,
  useRehberDurum,
  useRehberStore,
  type RehberAdimId,
} from './useRehber';

const ADIM_ICON: Record<RehberAdimId, LucideIcon> = {
  musteri: UserPlus,
  kartvizit: ScanLine,
  aiMail: Mail,
  rapor: BarChart3,
};

interface Props {
  open: boolean;
  onClose: () => void;
}

export function AiRehberiPanel({ open, onClose }: Props) {
  const { t } = useTranslation('rehber');
  const navigate = useNavigate();
  const { userId, durum, tamamlananSayi, toplam, yuzde, bitti } = useRehberDurum();
  const tamamla = useRehberStore((s) => s.tamamla);
  const geriAl = useRehberStore((s) => s.geriAl);
  const sifirla = useRehberStore((s) => s.sifirla);

  // Adım 1 (müşteri ekle) otomatik tespiti: kullanıcının GERÇEK verisine bak.
  // En az 1 müşteri varsa adım tamamlanmış say. Panel açıkken hafif bir
  // sorgu (perPage 1) yeterli; sonucu store ile senkronla.
  const { data: musteriOzet } = useMusteriler({ page: 1, perPage: 1 });
  useEffect(() => {
    if (!open || !userId) return;
    const toplamMusteri = musteriOzet?.meta.total ?? 0;
    if (toplamMusteri > 0 && !durum.tamamlanan.musteri) {
      tamamla(userId, 'musteri');
    }
  }, [open, userId, musteriOzet, durum.tamamlanan.musteri, tamamla]);

  // Panel açıkken arka plan kaydırmasını kilitle.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Esc ile kapat.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const adimTikla = (hedef: string) => {
    // rapor adımı için özel bir "üret" ekranı yok; panel = özet göstergeler.
    // Kullanıcıyı deep-link'e götür; gerçek aksiyonlar (kartvizit/e-posta)
    // ilgili sayfalarda tamamlanınca rehberTamamla ile işaretlenir.
    onClose();
    navigate(hedef);
  };

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label={t('baslik', { defaultValue: 'AI Rehberi' })}>
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 animate-in fade-in"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Sağdan açılan panel */}
      <aside className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col bg-card shadow-xl animate-in slide-in-from-right">
        {/* Başlık */}
        <div className="flex items-start justify-between border-b px-5 py-4">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Sparkles className="h-5 w-5" />
            </span>
            <div>
              <h2 className="text-base font-semibold leading-tight">
                {t('baslik', { defaultValue: 'AI Rehberi' })}
              </h2>
              <p className="text-xs text-muted-foreground">
                {t('altbaslik', { defaultValue: 'Hızlı başlangıç adımların' })}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Sade kredi sayacı: kullanılan/toplam — TL veya tavan gösterilmez */}
            <AiKrediChip />
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1.5 text-muted-foreground hover:bg-muted"
              aria-label={t('common:kapat', { defaultValue: 'Kapat' })}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* İlerleme çubuğu + yüzde */}
        <div className="border-b px-5 py-4">
          <div className="mb-1.5 flex items-center justify-between text-sm">
            <span className="font-medium">
              {t('ilerleme', {
                defaultValue: '{{tamam}} / {{toplam}} tamamlandı',
                tamam: tamamlananSayi,
                toplam,
              })}
            </span>
            <span className="font-semibold text-primary">%{yuzde}</span>
          </div>
          <div
            className="h-2.5 w-full overflow-hidden rounded-full bg-muted"
            role="progressbar"
            aria-valuenow={yuzde}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div
              className={cn(
                'h-full rounded-full transition-all duration-500 ease-out',
                bitti ? 'bg-success' : 'bg-primary',
              )}
              style={{ width: `${yuzde}%` }}
            />
          </div>
        </div>

        {/* İçerik */}
        <div className="flex-1 overflow-y-auto p-5">
          {bitti && (
            <div className="mb-4 flex flex-col items-center gap-2 rounded-xl border border-success/30 bg-success/10 px-4 py-6 text-center animate-in zoom-in">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-success/20 text-success">
                <Trophy className="h-7 w-7" />
              </span>
              <h3 className="flex items-center gap-1.5 text-lg font-bold text-success">
                <PartyPopper className="h-5 w-5" />
                {t('kutlama.baslik', { defaultValue: 'Tebrikler!' })}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t('kutlama.aciklama', {
                  defaultValue:
                    'Tüm başlangıç adımlarını tamamladın. Zoprey CRM’i keşfetmeye hazırsın!',
                })}
              </p>
            </div>
          )}

          <ol className="space-y-3">
            {REHBER_ADIMLAR.map((adim, i) => {
              const Icon = ADIM_ICON[adim.id];
              const done = !!durum.tamamlanan[adim.id];
              return (
                <li key={adim.id}>
                  <div
                    className={cn(
                      'group flex items-start gap-3 rounded-lg border p-3 transition-colors',
                      done
                        ? 'border-success/30 bg-success/5'
                        : 'border-border hover:border-primary/40 hover:bg-muted/50',
                    )}
                  >
                    {/* Durum işareti */}
                    <button
                      type="button"
                      onClick={() =>
                        userId &&
                        (done ? geriAl(userId, adim.id) : tamamla(userId, adim.id))
                      }
                      className={cn(
                        'mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition-colors',
                        done
                          ? 'border-success bg-success text-success-foreground'
                          : 'border-muted-foreground/40 text-transparent hover:border-primary',
                      )}
                      aria-label={
                        done
                          ? t('geriAl', { defaultValue: 'Tamamlanmadı olarak işaretle' })
                          : t('tamamla', { defaultValue: 'Tamamlandı olarak işaretle' })
                      }
                    >
                      <Check className="h-4 w-4" />
                    </button>

                    {/* Metin + aksiyon */}
                    <button
                      type="button"
                      onClick={() => adimTikla(adim.hedef)}
                      className="flex flex-1 items-start gap-3 text-left"
                    >
                      <span
                        className={cn(
                          'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
                          done
                            ? 'bg-success/10 text-success'
                            : 'bg-primary/10 text-primary',
                        )}
                      >
                        <Icon className="h-5 w-5" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-1.5">
                          <span className="text-xs font-medium text-muted-foreground">
                            {t('adimSira', { defaultValue: 'Adım {{n}}', n: i + 1 })}
                          </span>
                          {adim.stub && (
                            <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                              {t('yakinda', { defaultValue: 'yakında' })}
                            </span>
                          )}
                        </span>
                        <span
                          className={cn(
                            'block text-sm font-semibold',
                            done && 'text-muted-foreground line-through',
                          )}
                        >
                          {t(adim.i18n, { defaultValue: adim.baslik })}
                        </span>
                        <span className="block text-xs text-muted-foreground">
                          {t(adim.i18n.replace(/\.baslik$/, '.aciklama'), {
                            defaultValue: adim.aciklama,
                          })}
                        </span>
                      </span>
                      <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>

        {/* Alt bar */}
        <div className="flex items-center justify-between border-t px-5 py-3">
          <button
            type="button"
            onClick={() => userId && sifirla(userId)}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            {t('sifirla', { defaultValue: 'Sıfırla' })}
          </button>
          <span className="text-xs text-muted-foreground">
            {t('ipucu', { defaultValue: 'İstediğin an “?” butonundan geri dönebilirsin' })}
          </span>
        </div>
      </aside>
    </div>
  );
}
